// Sub-pages of the Bezug tab for the history (concept beans-and-shot-history §4 E and F, FR-27): "Verlauf" with
// filters by bean, period and taste, the key figures of the filter, the shots by day (a row opens the shot's page), and
// "Vergleich" of 2-3 shots.
// Decisions in core/screens/history.ts.
import { useEffect, useId, useState } from 'react';
import {
  DEFAULT_FILTER,
  compareBar,
  compareLanes,
  compareView,
  curveData,
  historyCalendar,
  historyPage,
  listErrorText,
  toggleCompare,
  type BrewPage,
  type HistoryGroup,
  type Period,
  type ShotFilter,
  type TasteFilter,
} from '../../core/index.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  ChipSelect,
  FactRow,
  LaneChart,
  NoteLine,
  Pending,
  Segmented,
} from '../components/index.js';
import { useMachine, useStore } from '../machine-context.js';
import { ShotDays, SubScreen } from './parts.js';

/**
 * The view and the chosen day of the calendar stay while the app runs: back from a shot opened in the calendar finds
 * the calendar as it was, not the list. The calendar shows its month whatever period the list had (Dominik 25.10.2026:
 * "das macht irgendwie kein sinn wenn man den ganzen kalender sieht und als filter heute hat"); the list's period waits
 * here and comes back with the list.
 */
const kept: { lens: 'list' | 'calendar'; day: number | null; period: Period } = {
  lens: 'list',
  day: null,
  period: DEFAULT_FILTER.period,
};

/** Back to the list and today (the screen gallery: its screens share this module and render one after another). */
export function resetHistoryView(): void {
  kept.lens = 'list';
  kept.day = null;
  kept.period = DEFAULT_FILTER.period;
}

export function HistoryPage({ onPage, onBack }: { onPage: (p: BrewPage) => void; onBack: () => void }) {
  const { ctx, client, machineId } = useMachine();
  const store = client.shotHistory(machineId);
  const hv = useStore(store);
  const beans = useStore(client.beans(machineId));
  const [choosing, setChoosing] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [lens, setLens] = useState<'list' | 'calendar'>(() => kept.lens);
  // after a switch of the view the new card fades in (M3: content swapped by a segmented button)
  const [swapped, setSwapped] = useState(false);
  const beanId = useId();
  const periodId = useId();
  const tasteId = useId();
  // the calendar's month or the list's period, then the first page (one request, not one per effect)
  useEffect(() => {
    const now = store.getSnapshot();
    const want =
      lens === 'calendar' ? 'month' : now.filter.period === 'month' ? kept.period : now.filter.period;
    if (want !== now.filter.period) {
      if (lens === 'calendar') kept.period = now.filter.period;
      void store.setFilter({ ...now.filter, period: want });
    } else if (!now.loaded) void store.refresh();
  }, [store, lens]);
  const v = historyPage(ctx, hv, beans);
  const bar = compareBar(ctx, chosen);
  const setFilter = (patch: Partial<ShotFilter>) => {
    void store.setFilter({ ...hv.filter, ...patch });
  };
  return (
    <SubScreen
      tab="bezug"
      title={v.title}
      onBack={onBack}
      testId="history-page"
      right={
        <button
          type="button"
          className="link-btn"
          aria-pressed={choosing}
          onClick={() => {
            setChoosing(!choosing);
            setChosen([]);
          }}
          data-testid="history-compare"
        >
          {ctx.tr(choosing ? 'cancel' : 'compare')}
        </button>
      }
    >
      <Segmented
        label={ctx.tr('viewLabel')}
        options={[
          { value: 'list' as const, label: ctx.tr('viewList'), on: lens === 'list' },
          { value: 'calendar' as const, label: ctx.tr('viewCalendar'), on: lens === 'calendar' },
        ]}
        onPick={(next) => {
          kept.lens = next;
          setLens(next);
          setSwapped(true);
        }}
        testId="history-lens"
      />
      {/* the filters as one row of chips (prototype "Verlauf"); a chip that narrows the list is filled */}
      <div className="chips" data-testid="history-filters">
        <ChipSelect
          id={beanId}
          label={v.filter.bean.label}
          text={v.filter.bean.options.find((o) => o.value === v.filter.bean.value)?.label ?? ''}
          on={v.filter.bean.value !== ''}
          value={v.filter.bean.value}
          onChange={(e) => setFilter({ bean: e.target.value || null })}
          data-testid="history-bean"
        >
          {v.filter.bean.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </ChipSelect>
        {/* the calendar is its month: no period beside it */}
        {lens === 'list' && (
          <ChipSelect
            id={periodId}
            label={v.filter.period.label}
            text={v.filter.period.options.find((o) => o.on)?.label ?? ''}
            value={v.filter.period.options.find((o) => o.on)?.value ?? 'all'}
            onChange={(e) => {
              const period = v.filter.period.options.find((o) => o.value === e.target.value)?.value;
              if (period) setFilter({ period });
            }}
            data-testid="history-period"
          >
            {v.filter.period.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </ChipSelect>
        )}
        <ChipSelect
          id={tasteId}
          label={v.filter.taste.label}
          text={(() => {
            const o = v.filter.taste.options.find((x) => x.on);
            return !o || o.value === 'all' ? v.filter.taste.label : o.label;
          })()}
          on={v.filter.taste.options.some((o) => o.on && o.value !== 'all')}
          value={String(v.filter.taste.options.find((o) => o.on)?.value ?? 'all')}
          onChange={(e) => setFilter({ taste: e.target.value as TasteFilter })}
          data-testid="history-taste"
        >
          {v.filter.taste.options.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </ChipSelect>
      </div>
      {v.stats && (
        <Card className="tab-card history-stats" data-testid="history-stats">
          <p className="history-count" data-testid="history-summary">
            <b className="num">{v.stats.count}</b> <span>{v.stats.countWord}</span>
            {v.stats.compare && (
              <>
                {' '}
                <span className="history-compare">{v.stats.compare}</span>
              </>
            )}
          </p>
          {v.stats.facts.length > 0 && <FactRow items={v.stats.facts} />}
          {v.stats.verdict && (
            <NoteLine icon="pulse" className="history-verdict" testId="history-verdict">
              {v.stats.verdict}
            </NoteLine>
          )}
        </Card>
      )}
      {lens === 'calendar' && (
        <CalendarCard
          className={swapped ? 'lens-in' : undefined}
          groups={v.groups}
          onOpen={(id) => onPage({ page: 'shot', id })}
          choosing={choosing}
          chosen={chosen}
          onToggle={(id) => setChosen(toggleCompare(chosen, id))}
        />
      )}
      {lens === 'list' && (
        <Card className={swapped ? 'tab-card lens-in' : 'tab-card'} data-testid="history-list">
          {v.loading && <Pending label={ctx.tr('loading')} />}
          {v.error && <Banner tone="danger">{listErrorText(ctx.lang, v.error)}</Banner>}
          <ShotDays
            groups={v.groups}
            onOpen={(id) => onPage({ page: 'shot', id })}
            choosing={choosing}
            chosen={chosen}
            onToggle={(id) => setChosen(toggleCompare(chosen, id))}
            rowTestId={(id) => `history-shot-${id}`}
            two
          />
          {v.empty && <p className="hint">{v.empty}</p>}
          {v.more && (
            <Button
              variant="secondary"
              size="sm"
              disabled={!v.more.enabled}
              onClick={() => void store.loadMore()}
              data-testid="history-more"
            >
              {v.more.label}
            </Button>
          )}
        </Card>
      )}
      {choosing && (
        <div className="compare-bar" data-testid="compare-bar">
          <span>{bar.text}</span>
          <Button
            size="sm"
            disabled={!bar.enabled}
            onClick={() => onPage({ page: 'compare', ids: chosen })}
            data-testid="compare-go"
          >
            {bar.label}
          </Button>
        </div>
      )}
    </SubScreen>
  );
}

/**
 * The month with a dot for every shot of a day (prototype "Verlauf", view "Kalender"). A day is a button: the chosen
 * one is filled (today when the calendar opens, as in the prototype), today keeps a ring, and the chosen day's shots
 * follow below the month, as rows of the list. The history then holds the month (period 'month'); its pages are loaded
 * one after another until the dots are complete.
 */
function CalendarCard({
  className,
  groups,
  onOpen,
  choosing,
  chosen,
  onToggle,
}: {
  className?: string;
  groups: readonly HistoryGroup[];
  onOpen: (id: string) => void;
  choosing: boolean;
  chosen: readonly string[];
  onToggle: (id: string) => void;
}) {
  const { ctx, client, machineId } = useMachine();
  const store = client.shotHistory(machineId);
  const hv = useStore(store);
  const cal = historyCalendar(ctx, hv.items);
  const [picked, setPicked] = useState<number | null>(() => kept.day);
  // a day kept from another month (the app open over the 1st) gives way to today
  const sel = picked !== null && cal.cells.some((c) => c.key === picked) ? picked : cal.today;
  const day = groups.filter((g) => g.day === sel);
  // one more page after each answer, never again after a failure (the list's "Mehr laden" is still there)
  const needMore = !cal.covered && hv.next !== null && !hv.loading && !hv.error;
  useEffect(() => {
    if (needMore) void store.loadMore();
  }, [needMore, hv.items.length, store]);
  return (
    <>
      <Card
        className={className ? `tab-card calendar ${className}` : 'tab-card calendar'}
        data-testid="history-calendar"
      >
        <CardHeader title={cal.title} info={{ text: ctx.tr('dotHint'), label: ctx.tr('infoShow') }} />
        <div className="cal-grid cal-head" aria-hidden="true">
          {cal.weekdays.map((w) => (
            <span key={w}>{w}</span>
          ))}
        </div>
        <div className="cal-grid">
          {cal.cells.map((c, i) =>
            c.day === null || c.key === null ? (
              <span key={`x${i}`} className="cal-day is-blank" />
            ) : (
              <button
                key={c.key}
                type="button"
                className={`cal-day${c.today ? ' is-today' : ''}${c.key === sel ? ' is-on' : ''}`}
                aria-pressed={c.key === sel}
                aria-label={ctx.tr('calDay', { d: c.day, n: c.dots })}
                disabled={c.later}
                onClick={() => {
                  kept.day = c.key;
                  setPicked(c.key);
                }}
                data-testid={`cal-day-${c.day}`}
              >
                <span className="num">{c.day}</span>
                <span className="cal-dots" aria-hidden="true">
                  {'•'.repeat(Math.min(5, c.dots))}
                </span>
              </button>
            ),
          )}
        </div>
      </Card>
      <Card className="tab-card" data-testid="history-calendar-day">
        {day.length > 0 ? (
          <ShotDays
            groups={day}
            onOpen={onOpen}
            choosing={choosing}
            chosen={chosen}
            onToggle={onToggle}
            rowTestId={(id) => `history-shot-${id}`}
            two
          />
        ) : (
          <p className="hint">{ctx.tr('calNone')}</p>
        )}
      </Card>
    </>
  );
}

export function ComparePage({ ids, onBack }: { ids: string[]; onBack: () => void }) {
  const { ctx, client, machineId } = useMachine();
  const shots = client.shots(machineId);
  const sv = useStore(shots);
  const beans = useStore(client.beans(machineId));
  const key = ids.join(',');
  useEffect(() => {
    for (const id of key.split(',')) void shots.curve(id);
  }, [key, shots]);
  const v = compareView(ctx, ids, sv.curves, beans, null);
  return (
    <SubScreen tab="bezug" title={v.title} onBack={onBack} testId="compare-page">
      <Card className="tab-card shot-curve" data-testid="compare-curves">
        {v.missing.length > 0 && <p className="hint">{ctx.tr('compareLoading')}</p>}
        <p className="legend compare-legend">
          {v.series.map((s, i) => (
            <span key={s.id} className={`cmp-key-${i}`}>
              {s.label}
            </span>
          ))}
        </p>
        <LaneChart
          view={compareLanes(
            ctx,
            ids.map((id, i) => ({
              label: v.series[i]?.label ?? '…',
              curve: sv.curves[id] ? curveData(sv.curves[id].curve) : undefined,
            })),
          )}
          testId="compare-chart"
        />
      </Card>
      <Card className="tab-card" data-testid="compare-figures">
        <table className="compare-table">
          <thead>
            <tr>
              <td />
              {v.series.map((s, i) => (
                <th key={s.id} scope="col" className={`cmp-col-${i}`}>
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {v.rows.map((r) => (
              <tr key={r.label}>
                <th scope="row">{r.label}</th>
                {r.cells.map((c, i) => (
                  <td key={i} className={c.diff ? 'is-diff' : undefined}>
                    {c.text}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </SubScreen>
  );
}
